import { HttpBackend, HttpClient, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { MaintenanceService, MaintenanceState } from '@services/maintenance.service';
import { catchError, of, switchMap, throwError } from 'rxjs';

// 503 is the API's own maintenance answer. 502, 504 and 0 (no response) are what the browser gets while the API is
// restarting during a deploy, but also after a single slow or failed request, so they only count as maintenance when
// the API fails to answer a status check too.
const UNREACHABLE_STATUSES = [0, 502, 504];

export const maintenanceInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const maintenanceService = inject(MaintenanceService);
  // Skips the interceptors, so a failing status check does not trigger another one.
  const backend = inject(HttpBackend);

  const enterMaintenance = (): void => {
    maintenanceService.enter();
    router.navigate(['/maintenance']);
  };

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || !req.url.startsWith(environment.baseApiUrl)) {
        return throwError(() => error);
      }

      if (error.status === 503) {
        enterMaintenance();

        return throwError(() => error);
      }

      // An offline browser also gets status 0, and that is not maintenance.
      if (UNREACHABLE_STATUSES.includes(error.status) && navigator.onLine) {
        return new HttpClient(backend).get<MaintenanceState>(`${environment.apiUrl}/maintenance`).pipe(
          catchError(() => of({ enabled: true })),
          switchMap(({ enabled }) => {
            if (enabled) {
              enterMaintenance();
            }

            return throwError(() => error);
          })
        );
      }

      return throwError(() => error);
    })
  );
};
